<template>
    <v-card class="scrollable-container pb-2 elevation-5">
        <v-card-title class="pb-0">
            <span>{{ title }}</span>
            <help-button :href="helpUrl"></help-button>
        </v-card-title>
        <v-card-text class="scrollable-content pb-0">
            <div class="px-3 pb-2">
                <v-expand-transition>
                    <div v-show="!isFormEditing">
                        <form-generator
                            v-model="venueData"
                            :schema="readSchema"
                        ></form-generator>
                    </div>
                </v-expand-transition>
                <v-expand-transition>
                    <v-form
                        v-show="isFormEditing"
                        ref="form"
                        v-model="isFormValid"
                    >
                        <form-generator
                            :value="venueData"
                            :schema="editSchema"
                            value-class=""
                            @input="onEditFormChange"
                        ></form-generator>
                    </v-form>
                </v-expand-transition>
            </div>
            <fieldset v-show="showChannelMaps" class="px-2 mb-2 mx-2">
                <legend
                    class="settings-section text-subtitle-2 px-1"
                    tabindex="0"
                >
                    Channel lineups
                </legend>
                <data-table-view
                    :headers="tableFields"
                    :items="venueChannelMaps"
                    :sort-by="sortBy"
                    :sort-desc="sortDesc"
                    :page="page"
                    :items-per-page="itemsPerPage"
                    :loading="channelMapsLoading"
                    :zebra-stripping="zebraStripping"
                    :computed-fields="computedFields"
                    :show-select="false"
                    :allow-export="false"
                >
                    <template v-slot:item.defaultChannel="{ item }">
                        <v-edit-dialog
                            v-if="isFormEditing && allowEditChannelMaps"
                            large
                            persistent
                            save-text="Set"
                            @save="onSetDefaultChannel(item)"
                            @open="onEditDefaultChannel(item)"
                            @close="onCloseDefaultChannel"
                        >
                            <v-btn
                                text
                                color="accent"
                                rounded
                                @focus="onFocusDefaultChannel"
                                >{{ item.defaultChannel }}</v-btn
                            >
                            <template v-slot:input>
                                <v-form
                                    v-model="isDefaultChannelValid"
                                    class="pt-3"
                                >
                                    <v-select
                                        v-model="editDefaultChannel"
                                        dense
                                        label="Default channel"
                                        :item-text="
                                            v =>
                                                `${v.channelNumber} - ${v.onScreenCallsign}`
                                        "
                                        item-value="channelNumber"
                                        :loading="isChannelOptionsLoading"
                                        :items="item.channelOptions"
                                        :rules="defaultChannelRules"
                                    ></v-select>
                                </v-form>
                            </template>
                        </v-edit-dialog>
                        <span v-else>{{ item.defaultChannel }}</span>
                    </template>
                </data-table-view>
            </fieldset>
        </v-card-text>
        <v-card-actions class="py-0 px-4">
            <v-container pa-0 fluid>
                <v-row no-gutters>
                    <v-col class="py-0 text-right">
                        <v-btn
                            v-if="showCancelButton"
                            outlined
                            small
                            class="mx-2"
                            cy-data="save"
                            @click="onCancelClick"
                        >
                            Cancel
                        </v-btn>
                        <v-btn
                            v-if="showSaveButton"
                            color="primary"
                            small
                            :disabled="!allowSave"
                            :loading="isSaveLoading"
                            cy-data="save"
                            @click="onSaveClick"
                        >
                            Save
                        </v-btn>
                    </v-col>
                </v-row>
            </v-container>
            <v-tooltip top>
                <template v-slot:activator="{ on }">
                    <v-btn
                        v-show="showEditButton"
                        fab
                        fixed
                        right
                        bottom
                        small
                        color="primary"
                        class="mb-5 mr-1"
                        aria-label="Edit venue settings"
                        cy-data="edit"
                        v-on="on"
                        @click="onEditSettingsClick"
                    >
                        <v-icon>{{ icons.edit }}</v-icon>
                    </v-btn>
                </template>
                <span>Edit venue settings</span>
            </v-tooltip>
        </v-card-actions>
        <confirmation-dialog ref="confirmationDialog"></confirmation-dialog>
    </v-card>
</template>

<script>
import { isEmpty } from '@/common/helpers';
import {
    checkPhone,
    checkZipCode,
    checkNumeric
} from '@/common/validationRules';
import usStates from '@/common/usStates';
import { mapState, mapActions } from 'vuex';
import { pick } from 'lodash';
import HocFormGenerator from '@/components/FormGenerator/HocFormGenerator';
import DataTableView from '@/components/Data/DataTableView';
import FormLayout from './forms/SettingsLayout';
import getReadSchema from './forms/formReadSchema';
import ConfirmationDialog from '@/components/Dialogs/ConfirmationPromiseDialog';
import HelpButton from '@/components/Buttons/HelpButton';

const FormGenerator = HocFormGenerator(FormLayout);

export default {
    name: 'SettingsPage',
    components: {
        'form-generator': FormGenerator,
        'data-table-view': DataTableView,
        'confirmation-dialog': ConfirmationDialog,
        'help-button': HelpButton
    },
    data() {
        return {
            title: 'Venue Settings',
            isFormValid: true,
            isFormDirty: false,
            isFormEditing: false,
            isChannelMapDirty: false,
            isSaveLoading: false,
            venueData: {},
            channelMapsLoading: false,
            lastFocusedElement: null,
            editDefaultChannel: null,
            isDefaultChannelValid: false,
            isChannelOptionsLoading: false,
            pmsStatus: {}
        };
    },
    computed: {
        ...mapState('venue', ['selectedVenueInfo']),
        ...mapState('channelMaps', ['channelMaps']),
        ...mapState('settings', [
            'tableFields',
            'computedFields',
            'requestQuery',
            'responseQuery',
            'zebraStripping',
            'rebootTimes',
            'helpUrl'
        ]),
        rebootTimeOptions() {
            return this.rebootTimes;
        },
        readSchema() {
            const showSupportEmail =
                this.venueData.supportEmail &&
                this.venueData.supportEmail.length;
            const showSupportWebUrl =
                this.venueData.supportWebUrl &&
                this.venueData.supportWebUrl.length;
            return getReadSchema(
                this.icons,
                showSupportEmail,
                showSupportWebUrl
            );
        },
        editSchema() {
            return [
                {
                    name: 'title',
                    hideLabel: true,
                    component: {
                        type: 'v-text-field',
                        props: {
                            label: 'Title *',
                            dense: true,
                            required: true,
                            maxlength: 254
                        }
                    }
                },
                {
                    name: 'billingAccountId',
                    hideLabel: true,
                    component: {
                        type: 'v-text-field',
                        props: {
                            label: 'Billing account Id',
                            dense: true,
                            maxlength: 36,
                            disabled: true
                        }
                    }
                },
                {
                    name: 'csgAccountId',
                    hideLabel: true,
                    component: {
                        type: 'v-text-field',
                        props: {
                            label: 'CSG account Id',
                            dense: true,
                            maxlength: 120,
                            disabled: true
                        }
                    }
                },
                {
                    name: 'pmsStatus',
                    hideLabel: true,
                    component: {
                        type: 'pms-status',
                        props: {
                            label: 'PMS Status',
                            dense: true,
                            maxlength: 120,
                            disabled: true
                        }
                    }
                },
                {
                    name: 'addr1',
                    hideLabel: true,
                    component: {
                        type: 'v-text-field',
                        props: {
                            label: 'Address 1',
                            dense: true,
                            maxlength: 254
                        }
                    }
                },
                {
                    name: 'addr2',
                    hideLabel: true,
                    component: {
                        type: 'v-text-field',
                        props: {
                            label: 'Address 2',
                            dense: true,
                            maxlength: 254
                        }
                    }
                },
                {
                    name: 'city',
                    hideLabel: true,
                    component: {
                        type: 'v-text-field',
                        props: {
                            label: 'City *',
                            dense: true,
                            required: true,
                            maxlength: 100
                        }
                    }
                },
                {
                    name: 'state',
                    hideLabel: true,
                    component: {
                        type: 'v-autocomplete',
                        props: {
                            label: 'State *',
                            dense: true,
                            required: true,
                            maxlength: 50,
                            items: this.stateOptions,
                            itemText: 'name',
                            itemValue: 'abbreviation'
                        }
                    }
                },
                {
                    name: 'zip',
                    hideLabel: true,
                    component: {
                        type: 'v-text-field',
                        props: {
                            label: 'Zip',
                            dense: true,
                            maxlength: 20,
                            rules: [v => checkZipCode(v, 'Zip')]
                        }
                    }
                },
                {
                    name: 'phone',
                    hideLabel: true,
                    component: {
                        type: 'v-text-field',
                        props: {
                            label: 'Phone',
                            dense: true,
                            mask: '(###) ###-####',
                            rules: [v => checkPhone(v, 'Phone')]
                        }
                    }
                },
                {
                    name: 'defaultPublicChannelMapId',
                    hideLabel: true,
                    component: {
                        type: 'v-select',
                        props: {
                            label: 'Default public channel lineup *',
                            dense: true,
                            palceholder: 'Select a channel lineup',
                            items: this.publicChannelMaps,
                            itemText: 'name',
                            itemValue: 'id',
                            required: this.venueData.defaultPrivateChannelMapId
                                ? false
                                : true,
                            clearable: true
                        }
                    }
                },
                {
                    name: 'defaultPrivateChannelMapId',
                    hideLabel: true,
                    component: {
                        type: 'v-select',
                        props: {
                            label: 'Default private channel lineup *',
                            dense: true,
                            placeholder: 'Select a channel lineup',
                            items: this.privateChannelMaps,
                            itemText: 'name',
                            itemValue: 'id',
                            required: this.venueData.defaultPublicChannelMapId
                                ? false
                                : true,
                            clearable: true
                        }
                    }
                },
                {
                    name: 'rebootTime',
                    hideLabel: true,
                    component: {
                        type: 'v-select',
                        props: {
                            label: 'Reboot time *',
                            dense: true,
                            items: this.rebootTimeOptions,
                            required: true
                        }
                    }
                },
                {
                    name: 'ipv4AddressRange',
                    hideLabel: true,
                    component: {
                        type: 'v-text-field',
                        props: {
                            label: 'IP4 Range',
                            dense: true,
                            disabled: !this.allowEditSupportInfo
                        }
                    }
                },
                {
                    name: 'ipv6AddressRange',
                    hideLabel: true,
                    component: {
                        type: 'v-text-field',
                        props: {
                            label: 'IP6 Range',
                            dense: true,
                            disabled: !this.allowEditSupportInfo
                        }
                    }
                },
                {
                    name: 'supportNumber',
                    hideLabel: true,
                    component: {
                        type: 'v-text-field',
                        props: {
                            label: 'Support number',
                            dense: true,
                            mask: '(###) ###-####',
                            hint: 'for example: (1800) 123-4567',
                            disabled: !this.allowEditSupportInfo,
                            rules: [v => checkPhone(v, 'Support number')]
                        }
                    }
                }
            ];
        },
        sortBy() {
            return this.requestQuery.sort;
        },
        sortDesc() {
            return this.requestQuery.desc;
        },
        page() {
            return this.requestQuery.number;
        },
        itemsPerPage() {
            return this.requestQuery.size;
        },
        showEditButton() {
            return (
                !this.isFormEditing &&
                this.$can('edit', 'VenueInfo') &&
                this.$can('show', 'Buttons/settings/editSettings')
            );
        },
        showCancelButton() {
            return this.isFormEditing;
        },
        showSaveButton() {
            return this.isFormEditing;
        },
        showChannelMaps() {
            return this.$can('read', 'VenueChannelMap');
        },
        allowSave() {
            return (
                (this.isFormDirty || this.isChannelMapDirty) &&
                this.isFormValid &&
                !this.isSaveLoading
            );
        },
        allowEditChannelMaps() {
            return this.$can('edit', 'VenueDefaultChannel');
        },
        publicChannelMaps() {
            return this.venueChannelMaps.filter(
                item => item.visibility === 'PUBLIC'
            );
        },
        privateChannelMaps() {
            return this.venueChannelMaps.filter(
                item => item.visibility === 'PRIVATE'
            );
        },
        stateOptions() {
            return usStates;
        },
        allowEditSupportInfo() {
            return (
                this.$can('edit', 'VenueInfo') &&
                this.$can('show', 'Buttons/settings/editAdmin')
            );
        },
        defaultChannelRules() {
            return [v => checkNumeric(v)];
        },
        venueChannelMaps() {
            return this.channelMaps.map(item => {
                return {
                    ...item,
                    name: item.displayName || item.locationName
                };
            });
        }
    },
    watch: {
        selectedVenueInfo: {
            immediate: true,
            handler(value) {
                if (!isEmpty(value)) {
                    this.venueData = this.copyVenueData(value);
                    setTimeout(() => {
                        this.isFormDirty = false;
                    }, 100);
                }
            }
        }
    },
    created() {
        if (this.tableFields && !this.tableFields.length) {
            this.setupView().then(() => {
                this.fetchSettingsOptions();
            });
        }
    },
    mounted() {
        //FetchPmsStatus
        if (
            this.selectedVenueInfo &&
            this.selectedVenueInfo.pmsIntegrationAvailable
        ) {
            this.fetchPmsStatus().then(res => {
                this.pmsStatus = res.data;
                this.venueData.pmsStatus = res.data;
            });
        }
    },
    methods: {
        ...mapActions('settings', [
            'setupView',
            'fetchSettingsOptions',
            'updateVenueEntity'
        ]),
        ...mapActions('channelMaps', ['fetchChannelMapOptions']),
        ...mapActions('venue', ['fetchPmsStatus']),
        // ====================== Events ========================
        onEditSettingsClick() {
            this.isFormEditing = true;
            this.$refs.form.validate();
            this.setFormFocus();
        },
        onCancelClick() {
            if (this.isFormDirty) {
                this.$refs.confirmationDialog
                    .open(
                        'Edit settings',
                        'Are you sure, you want to close without saving',
                        'Form data has been modified',
                        {
                            confirmActionText: 'Save changes',
                            cancelActionText: 'Cancel changes',
                            showIcon: true
                        }
                    )
                    .then(res => {
                        if (res === true) {
                            this.saveThisVenue();
                        } else {
                            this.resetFormData();
                        }
                    });
            } else {
                this.isFormEditing = false;
            }
            this.setFormFocus();
        },
        onSaveClick() {
            this.saveThisVenue();
            this.setFormFocus();
        },
        onEditFormChange(value) {
            this.venueData = { ...value };
            this.isFormDirty = true;
            this.$nextTick(() => {
                this.$refs.form.validate();
            });
        },
        onSetDefaultChannel(item) {
            if (this.isDefaultChannelValid) {
                this.$set(
                    item,
                    'defaultChannel',
                    Number(this.editDefaultChannel) || 0
                );
                this.isChannelMapDirty = true;
                this.editDefaultChannel = null;
            }
        },
        onEditDefaultChannel(item) {
            this.editDefaultChannel = item.defaultChannel;
            if (!(item.channelOptions && item.channelOptions.length > 0)) {
                this.isChannelOptionsLoading = true;
                this.fetchChannelMapOptions({ channelMapId: item.id })
                    .then(res => {
                        this.$set(item, 'channelOptions', res.data.channels);
                    })
                    .finally(() => {
                        this.isChannelOptionsLoading = false;
                    });
            }
        },
        onCloseDefaultChannel() {
            this.setNextFocus(this.lastFocusedElement);
        },
        onFocusDefaultChannel(event) {
            this.lastFocusedElement = event.target;
        },
        copyVenueData(data) {
            return {
                ...pick(data, [
                    'id',
                    'title',
                    'billingAccountId',
                    'csgAccountId',
                    'addr1',
                    'addr2',
                    'city',
                    'state',
                    'zip',
                    'phone',
                    'rebootTime',
                    'ipv4AddressRange',
                    'ipv6AddressRange',
                    'defaultPublicChannelMapId',
                    'defaultPrivateChannelMapId',
                    'supportNumber',
                    'supportEmail',
                    'supportWebUrl',
                    'pmsStatus'
                ]),
                pmsStatus: this.pmsStatus,
                ipv4AddressRange: this.getArrayStringValue(
                    data.ipv4AddressRange
                ),
                ipv6AddressRange: this.getArrayStringValue(
                    data.ipv6AddressRange
                ),
                stateName: this.getStateName(data.state),
                defaultPublicChannelMapName: this.getChannelMapName(
                    data.defaultPublicChannelMap
                ),
                defaultPrivateChannelMapName: this.getChannelMapName(
                    data.defaultPrivateChannelMap
                )
            };
        },
        resetFormData() {
            this.isFormValid = true;
            this.isFormEditing = false;
            this.isSaveLoading = false;
            if (this.isFormDirty) {
                this.venueData = this.copyVenueData(this.selectedVenueInfo);
            }
            setTimeout(() => {
                this.isFormDirty = false;
                this.isChannelMapDirty = false;
            }, 100);
        },
        getChannelMapName(channelMap) {
            if (channelMap) {
                return channelMap.displayName || channelMap.locationName;
            }
            return 'Unassigned';
        },
        getStateName(stateCode) {
            const state = this.stateOptions.find(
                item => item.abbreviation === stateCode
            );
            if (state) {
                return state.name;
            }
            return '';
        },
        getArrayStringValue(ipRangeArray) {
            if (ipRangeArray) {
                return ipRangeArray.join(', ');
            }
            return '';
        },
        saveThisVenue() {
            if (this.isFormDirty || this.isChannelMapDirty) {
                const payload = {
                    oldVenue: this.selectedVenueInfo,
                    newVenue: this.venueData,
                    oldChannelMaps: this.channelMaps,
                    newChannelMaps: this.venueChannelMaps
                };
                this.isSaveLoading = true;
                this.channelMapsLoading = true;
                this.updateVenueEntity(payload)
                    .then(() => {
                        this.resetFormData();
                    })
                    .finally(() => {
                        this.isSaveLoading = false;
                        this.channelMapsLoading = false;
                    });
            }
        },
        setNextFocus(sourceElement) {
            const keydownEsc = new KeyboardEvent('keydown', { keyCode: 27 });
            this.$nextTick(() => {
                if (sourceElement) {
                    const button = sourceElement.closest('button');
                    button.focus();
                    button.dispatchEvent(keydownEsc);
                }
            });
        },
        setFormFocus() {
            this.$nextTick(() => {
                const section = this.$el.querySelector('.settings-section');
                if (section) {
                    section.focus();
                }
            });
        }
    }
};
</script>

<style scoped lang="scss">
::v-deep legend {
    outline: none;

    &:focus {
        background-color: rgba(map-get($deep-grey, 'base'), 0.2);
        border-radius: 10%;
        color: var(--v-primary-base);
    }
}
</style>
